import { fromMarkdown } from "mdast-util-from-markdown";
import { gfmFromMarkdown } from "mdast-util-gfm";
import { gfm } from "micromark-extension-gfm";

/** Offsets use JavaScript string indices (UTF-16 code units) in the raw message text. */
export interface QuoteRange {
  source_quote_start: number;
  source_quote_end: number;
  source_quote_prefix: string;
  source_quote_suffix: string;
}

export interface QuoteAnchor {
  source_quote: string;
  source_quote_sha256?: string | null;
  source_quote_start?: number | null;
  source_quote_end?: number | null;
  source_quote_prefix?: string | null;
  source_quote_suffix?: string | null;
}

/** Verify an optional persisted quote digest before using an offsetless anchor. */
export async function quoteHashMatches(anchor: QuoteAnchor): Promise<boolean> {
  if (anchor.source_quote_sha256 == null) return true;
  if (!/^[a-f0-9]{64}$/i.test(anchor.source_quote_sha256) || !globalThis.crypto?.subtle) return false;
  try {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(anchor.source_quote));
    const hex = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
    return hex === anchor.source_quote_sha256.toLowerCase();
  } catch {
    return false;
  }
}

const CONTEXT_LENGTH = 32;

function suffixMatch(left: string, right: string): number {
  let count = 0;
  while (count < left.length && count < right.length
    && left[left.length - 1 - count] === right[right.length - 1 - count]) count++;
  return count;
}

function prefixMatch(left: string, right: string): number {
  let count = 0;
  while (count < left.length && count < right.length && left[count] === right[count]) count++;
  return count;
}

function occurrences(text: string, quote: string): number[] {
  if (!quote) return [];
  const result: number[] = [];
  let from = 0;
  while (from <= text.length - quote.length) {
    const at = text.indexOf(quote, from);
    if (at < 0) break;
    result.push(at);
    from = at + 1;
  }
  return result;
}

interface CharacterSpan { start: number; end: number }

interface TextProjection { text: string; spans: CharacterSpan[] }

/** Fold layout whitespace while retaining positions in the original string. */
function canonical(text: string, spans?: CharacterSpan[]): TextProjection {
  let value = "";
  const mapped: CharacterSpan[] = [];
  for (let i = 0; i < text.length; i++) {
    const space = /\s/.test(text[i]);
    if (space && value.endsWith(" ")) {
      mapped[mapped.length - 1].end = spans?.[i]?.end ?? i + 1;
      continue;
    }
    value += space ? " " : text[i];
    mapped.push(spans?.[i] ?? { start: i, end: i + 1 });
  }
  return { text: value, spans: mapped };
}

/** Use the same Markdown grammar as Streamdown to retain raw source positions. */
function markdownText(raw: string): TextProjection {
  const root = fromMarkdown(raw, { extensions: [gfm()], mdastExtensions: [gfmFromMarkdown()] });
  const leaves: Array<{ value: string; start: number; end: number }> = [];
  const visit = (node: typeof root | (typeof root.children)[number]) => {
    if ("children" in node) {
      for (const child of node.children) visit(child);
    } else if ((node.type === "text" || node.type === "inlineCode")
      && node.position?.start.offset != null && node.position.end.offset != null) {
      leaves.push({ value: node.value, start: node.position.start.offset, end: node.position.end.offset });
    }
  };
  visit(root);
  let text = "";
  const spans: CharacterSpan[] = [];
  let previousEnd = 0;
  for (const leaf of leaves) {
    const gap = raw.slice(previousEnd, leaf.start);
    const lineBreak = gap.indexOf("\n");
    if (text && lineBreak >= 0) {
      text += "\n";
      spans.push({ start: previousEnd + lineBreak, end: previousEnd + lineBreak + 1 });
    }
    const source = raw.slice(leaf.start, leaf.end);
    let cursor = 0;
    const local: CharacterSpan[] = [];
    for (let i = 0; i < leaf.value.length; i++) {
      const character = leaf.value[i];
      let at = source.indexOf(character, cursor);
      if (at < 0) at = cursor;
      local.push({ start: leaf.start + at, end: Math.min(leaf.end, leaf.start + at + 1) });
      cursor = at + character.length;
    }
    // A leaf can decode escapes/entities or remove inline-code delimiters.
    // Assign those hidden source characters to the adjacent visible character.
    if (local.length) {
      local[0].start = leaf.start;
      for (let i = 0; i < local.length - 1; i++) local[i].end = local[i + 1].start;
      local[local.length - 1].end = leaf.end;
    }
    text += leaf.value;
    spans.push(...local);
    previousEnd = leaf.end;
  }
  return canonical(text, spans);
}

function projectedOccurrences(projection: TextProjection, quote: string): Array<{ start: number; end: number }> {
  const normalized = canonical(quote).text;
  return occurrences(projection.text, normalized).map((at) => ({
    start: projection.spans[at].start,
    end: projection.spans[at + normalized.length - 1].end,
  }));
}

function chooseOccurrence(
  text: string,
  quote: string,
  before: string,
  after: string,
  requireContext = false,
): number | null {
  const candidates = occurrences(text, quote);
  if (candidates.length === 1 && !requireContext) return candidates[0];
  let best = -1;
  let bestScore = 0;
  let tied = false;
  for (const at of candidates) {
    const score = suffixMatch(text.slice(Math.max(0, at - CONTEXT_LENGTH), at), before)
      + prefixMatch(text.slice(at + quote.length, at + quote.length + CONTEXT_LENGTH), after);
    if (score > bestScore) {
      best = at;
      bestScore = score;
      tied = false;
    } else if (score === bestScore) {
      tied = true;
    }
  }
  // One coincidental space or punctuation mark is not enough to recover a
  // moved range or disambiguate repeated text.
  return bestScore >= 4 && !tied ? best : null;
}

/** Capture a selected rendered quote as an unambiguous range in source text. */
export function captureQuoteRange(
  rawText: string,
  quote: string,
  renderedBefore: string,
  renderedAfter: string,
): QuoteRange | null {
  const source = markdownText(rawText);
  const rawMatches = projectedOccurrences(source, quote);
  const rendered = canonical(renderedBefore + quote + renderedAfter).text;
  const normalizedQuote = canonical(quote).text;
  const renderedMatches = occurrences(rendered, normalizedQuote);
  const selectedAt = canonical(renderedBefore).text.length;
  const ordinal = renderedMatches.indexOf(selectedAt);
  const candidate = rawMatches.length === renderedMatches.length && ordinal >= 0
    ? rawMatches[ordinal]
    : null;
  const chosenAt = candidate ? null : chooseOccurrence(source.text, normalizedQuote,
    canonical(renderedBefore).text, canonical(renderedAfter).text);
  const chosen = candidate ?? (chosenAt === null ? null : rawMatches[occurrences(source.text, normalizedQuote).indexOf(chosenAt)]);
  if (!chosen) return null;
  return {
    source_quote_start: chosen.start,
    source_quote_end: chosen.end,
    source_quote_prefix: rawText.slice(Math.max(0, chosen.start - CONTEXT_LENGTH), chosen.start),
    source_quote_suffix: rawText.slice(chosen.end, chosen.end + CONTEXT_LENGTH),
  };
}

/** Validate persisted offsets, then recover a moved quote using its context. */
export function resolveQuoteRange(rawText: string, anchor: QuoteAnchor): QuoteRange | null {
  const { source_quote: quote, source_quote_start: start, source_quote_end: end } = anchor;
  if (!quote || !Number.isSafeInteger(start) || !Number.isSafeInteger(end)) return null;
  const matches = projectedOccurrences(markdownText(rawText), quote);
  if (start! >= 0 && matches.some((match) => match.start === start && match.end === end)) {
    return {
      source_quote_start: start!, source_quote_end: end!,
      source_quote_prefix: anchor.source_quote_prefix ?? "",
      source_quote_suffix: anchor.source_quote_suffix ?? "",
    };
  }
  const prefix = anchor.source_quote_prefix ?? "";
  const suffix = anchor.source_quote_suffix ?? "";
  if (!prefix && !suffix) return null;
  let best: { start: number; end: number } | null = null;
  let score = 0;
  let tied = false;
  for (const match of matches) {
    const current = suffixMatch(rawText.slice(Math.max(0, match.start - CONTEXT_LENGTH), match.start), prefix)
      + prefixMatch(rawText.slice(match.end, match.end + CONTEXT_LENGTH), suffix);
    if (current > score) { best = match; score = current; tied = false; }
    else if (current === score) tied = true;
  }
  return !best || score < 4 || tied ? null : {
    source_quote_start: best.start,
    source_quote_end: best.end,
    source_quote_prefix: prefix,
    source_quote_suffix: suffix,
  };
}

/** Map a validated source span to visible text, retaining its DOM string range. */
export function renderedQuoteRange(rawText: string, renderedText: string, anchor: QuoteAnchor): { start: number; end: number } | null {
  const visible = canonical(renderedText);
  const quote = canonical(anchor.source_quote).text;
  const renderedMatches = occurrences(visible.text, quote);
  if (!renderedMatches.length) return null;
  let ordinal: number;
  if (anchor.source_quote_start == null && anchor.source_quote_end == null) {
    if (renderedMatches.length !== 1) return null;
    ordinal = 0;
  } else {
    const range = resolveQuoteRange(rawText, anchor);
    if (!range) return null;
    const sourceMatches = projectedOccurrences(markdownText(rawText), anchor.source_quote);
    ordinal = sourceMatches.findIndex((match) => match.start === range.source_quote_start
      && match.end === range.source_quote_end);
    if (ordinal < 0 || sourceMatches.length !== renderedMatches.length) return null;
  }
  const at = renderedMatches[ordinal];
  return { start: visible.spans[at].start, end: visible.spans[at + quote.length - 1].end };
}

/** Compatibility helper for callers that only need the first visible index. */
export function renderedQuoteStart(rawText: string, renderedText: string, anchor: QuoteAnchor): number | null {
  return renderedQuoteRange(rawText, renderedText, anchor)?.start ?? null;
}
